/**
 * Primitives shared by the FanLamp / LampSmart codecs.
 *
 * Ported 1:1 from NicoIIT/ha-ble-adv
 * (custom_components/ble_adv/codecs/utils.py and fanlamp.py).
 */
/** BLE-style LFSR whitening. Self-inverse: whiten(whiten(x, s), s) === x. */
export function whiten(buffer, seed) {
  const out = new Uint8Array(buffer.length);
  let r = seed;
  for (let i = 0; i < buffer.length; i++) {
    let b = 0;
    for (let j = 0; j < 8; j++) {
      r <<= 1;
      if (r & 0x80) {
        r ^= 0x11;
        b |= 1 << j;
      }
      r &= 0x7f;
    }
    out[i] = buffer[i] ^ b;
  }
  return out;
}
/** Reverse the bit order of one byte: 1100_1010 -> 0101_0011. */
export function reverseByte(x) {
  x = ((x & 0x55) << 1) | ((x & 0xaa) >> 1);
  x = ((x & 0x33) << 2) | ((x & 0xcc) >> 2);
  return (((x & 0x0f) << 4) | ((x & 0xf0) >> 4)) & 0xff;
}
export function reverseAll(buffer) {
  const out = new Uint8Array(buffer.length);
  for (let i = 0; i < buffer.length; i++) out[i] = reverseByte(buffer[i]);
  return out;
}
/**
 * CRC-16/XMODEM with an arbitrary initial value — Python's `binascii.crc_hqx`.
 * Poly 0x1021, MSB-first, no reflection, no final xor.
 */
export function crc16(buffer, seed) {
  let crc = seed & 0xffff;
  for (let i = 0; i < buffer.length; i++) {
    crc ^= (buffer[i] << 8) & 0xffff;
    for (let j = 0; j < 8; j++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc & 0xffff;
}
// ---------------------------------------------------------------------------
// AES-128 ECB, single-block encryption only.
//
// The V2 codec signs each packet with the first two bytes of one AES block.
// That is the entire cryptographic requirement, so rather than pull in a
// native crypto dependency we implement the forward direction here.
// ---------------------------------------------------------------------------
const SBOX = new Uint8Array(256);
const RCON = [0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40, 0x80, 0x1b, 0x36];
(function buildSbox() {
  // Multiplicative inverse in GF(2^8) followed by the affine transform.
  const p = new Uint8Array(256);
  const inv = new Uint8Array(256);
  let x = 1;
  for (let i = 0; i < 255; i++) {
    p[i] = x;
    inv[x] = i;
    x ^= (x << 1) ^ (x & 0x80 ? 0x11b : 0); // x *= 3 in GF(2^8)
    x &= 0xff;
  }
  SBOX[0] = 0x63;
  for (let i = 1; i < 256; i++) {
    // i^-1 == 3^(255 - log3(i)); the exponent is mod 255, and 255 wraps to 0.
    const b = p[(255 - inv[i]) % 255];
    let s = b;
    let r = b;
    for (let k = 0; k < 4; k++) {
      r = ((r << 1) | (r >> 7)) & 0xff;
      s ^= r;
    }
    SBOX[i] = s ^ 0x63;
  }
})();
/** Multiply by x in GF(2^8) with the AES reduction polynomial. */
function xtime(a) {
  return ((a << 1) ^ (a & 0x80 ? 0x1b : 0)) & 0xff;
}
function expandKey(key) {
  const rk = new Uint8Array(176); // 11 round keys x 16 bytes
  rk.set(key.subarray(0, 16));
  for (let i = 16; i < 176; i += 4) {
    let t0 = rk[i - 4];
    let t1 = rk[i - 3];
    let t2 = rk[i - 2];
    let t3 = rk[i - 1];
    if (i % 16 === 0) {
      // RotWord + SubWord + Rcon
      const tmp = t0;
      t0 = SBOX[t1] ^ RCON[i / 16 - 1];
      t1 = SBOX[t2];
      t2 = SBOX[t3];
      t3 = SBOX[tmp];
    }
    rk[i] = rk[i - 16] ^ t0;
    rk[i + 1] = rk[i - 15] ^ t1;
    rk[i + 2] = rk[i - 14] ^ t2;
    rk[i + 3] = rk[i - 13] ^ t3;
  }
  return rk;
}
/** Encrypt exactly one 16-byte block with a 16-byte key (AES-128-ECB). */
export function aes128EncryptBlock(key, block) {
  if (key.length !== 16) throw new Error(`AES-128 key must be 16 bytes, got ${key.length}`);
  if (block.length !== 16) throw new Error(`AES block must be 16 bytes, got ${block.length}`);
  const rk = expandKey(key);
  const s = new Uint8Array(block);
  for (let i = 0; i < 16; i++) s[i] ^= rk[i];
  for (let round = 1; round <= 10; round++) {
    for (let i = 0; i < 16; i++) s[i] = SBOX[s[i]];
    // ShiftRows (state is column-major: byte index = 4*col + row)
    let t = s[1];
    s[1] = s[5];
    s[5] = s[9];
    s[9] = s[13];
    s[13] = t;
    t = s[2];
    s[2] = s[10];
    s[10] = t;
    t = s[6];
    s[6] = s[14];
    s[14] = t;
    t = s[15];
    s[15] = s[11];
    s[11] = s[7];
    s[7] = s[3];
    s[3] = t;
    if (round !== 10) {
      for (let c = 0; c < 16; c += 4) {
        const a0 = s[c],
          a1 = s[c + 1],
          a2 = s[c + 2],
          a3 = s[c + 3];
        const all = a0 ^ a1 ^ a2 ^ a3;
        s[c] ^= all ^ xtime(a0 ^ a1);
        s[c + 1] ^= all ^ xtime(a1 ^ a2);
        s[c + 2] ^= all ^ xtime(a2 ^ a3);
        s[c + 3] ^= all ^ xtime(a3 ^ a0);
      }
    }
    const off = round * 16;
    for (let i = 0; i < 16; i++) s[i] ^= rk[off + i];
  }
  return s;
}
